import { isDefined, isNotDefined } from '@sgme/fp';
import { Select, Tooltip } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import clsx from 'clsx';
import deepEqual from 'fast-deep-equal';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { userApi } from '@/api/user/index.ts';
import { ForwardSwitch } from '@/App/tiles/CashTile/ForwardTile/ForwardSwitch.tsx';
import { useCheckSwapByTradeCapture } from '@/App/tiles/Swap/SwapContext.tsx';
import { FormattedFXDate } from '@/components/ui/FormattedFXDate.tsx';
import { getConfig } from '@/config/config.ts';
import { useAppSelector } from '@/store/hooks.ts';
import { selectSwapTile } from '@/store/swap/selectors/tile.ts';
import type { IntlMessageId } from '@/types/intl';

const NDF_LOCAL_MARKET_CURRENCIES = getConfig().app?.ndfLocalMarketCurrencies;

export type NDSSwapFieldsProps = {
  tileId: string;
};

export const NDSSwapFields = (props: NDSSwapFieldsProps) => {
  const { tileId } = props;

  const { sgwtWebAnalytics } = useSgwtWidgets();

  const checkSwapWithTradeCapture = useCheckSwapByTradeCapture();

  const {
    currency1,
    currency2,
    isLocalMarket,
    onshoreSettlementCcy,
    offshoreSettlementCcy,
    isCrossed,
    fixingCurrency,
    xCurrency,
    possibleXCurrencies,
    fixingSource,
    defaultFixingSource,
    possibleFixingSources,
    isFixingSourceOverriden,
    sndFixingSource,
    defaultSndFixingSource,
    possibleSndFixingSources,
    isSndFixingSourceOverriden,
    nearFixingDate,
    farFixingDate,
    tradeCapture,
  } = useAppSelector((state) => {
    const tile = selectSwapTile(state, tileId);

    const localMarketsCcy = userApi.endpoints.currentUser.select()(state).data?.localMarketsCcy ?? [];

    if (isNotDefined(tile) || !tile.isNonDeliverable) {
      return {
        currency1: '',
        currency2: '',
        isLocalMarket: false,
        onshoreSettlementCcy: '',
        offshoreSettlementCcy: '',
        isCrossed: false,
        fixingCurrency: '',
        xCurrency: '',
        possibleXCurrencies: [],
        fixingSource: '',
        possibleFixingSources: [],
        isFixingSourceOverriden: false,
        sndFixingSource: '',
        possibleSndFixingSources: [],
        isSndFixingSourceOverriden: false,
        nearFixingDate: undefined,
        farFixingDate: undefined,
        tradeCapture: undefined,
      };
    }

    const currency1 = tile.properties.currency1 ?? '';
    const currency2 = tile.properties.currency2 ?? '';
    const isCcy1LocalMarket = NDF_LOCAL_MARKET_CURRENCIES?.includes(currency1) && localMarketsCcy?.includes(currency1);
    const isCcy2LocalMarket = NDF_LOCAL_MARKET_CURRENCIES?.includes(currency2) && localMarketsCcy?.includes(currency2);
    const onshoreSettlementCcy = isCcy1LocalMarket ? currency1 : currency2;
    const offshoreSettlementCcy = isCcy1LocalMarket ? currency2 : currency1;
    const isLocalMarket = isCcy1LocalMarket || isCcy2LocalMarket;

    return {
      currency1,
      currency2,
      isLocalMarket,
      onshoreSettlementCcy,
      offshoreSettlementCcy,

      isCrossed: tile.properties.isCrossed,

      fixingCurrency: tile.properties.fixingCurrency,

      // xCurrency
      xCurrency: tile.properties.xCurrency,

      possibleXCurrencies: tile.properties.possibleXCurrencies?.possible.toSorted() ?? [],

      // fixingSource
      fixingSource: tile.properties.fixingSource,
      defaultFixingSource: tile.properties.possibleFixingSources?.default,

      possibleFixingSources: tile.properties.possibleFixingSources?.possible.toSorted() ?? [],

      isFixingSourceOverriden: tile.properties.fixingSource !== tile.properties.possibleFixingSources?.default,

      // sndFixingSource
      sndFixingSource: tile.properties.sndFixingSource,
      defaultSndFixingSource: tile.properties.possibleSndFixingSources?.default,

      possibleSndFixingSources: tile.properties.possibleSndFixingSources?.possible.toSorted() ?? [],

      isSndFixingSourceOverriden: tile.properties.sndFixingSource !== tile.properties.possibleSndFixingSources?.default,

      // dates
      nearFixingDate: tile.properties.nearFixingDate,
      farFixingDate: tile.properties.farFixingDate,

      tradeCapture: tile.tradeCapture,
    };
  }, deepEqual);

  const marketType = fixingCurrency === onshoreSettlementCcy ? 'Onshore' : 'Offshore';

  const handleMarketTypeChange = useCallback(
    (value: 'Onshore' | 'Offshore') => {
      if (isNotDefined(tradeCapture)) {
        return;
      }

      const settlementCurrency = value === 'Onshore' ? onshoreSettlementCcy : offshoreSettlementCcy;

      sgwtWebAnalytics?.trackEvent('Swap', 'Market type changed', value);

      checkSwapWithTradeCapture({
        fixingCurrency: settlementCurrency,
      });
    },
    [tradeCapture, onshoreSettlementCcy, offshoreSettlementCcy, checkSwapWithTradeCapture, sgwtWebAnalytics],
  );

  const handleXCurrencyChange = (value: string | undefined) => {
    sgwtWebAnalytics?.trackEvent('Swap', 'X currency changed', value);

    checkSwapWithTradeCapture({
      xCurrency: value,
    });
  };

  const handleFixingSourceChange = (value: string | undefined) => {
    sgwtWebAnalytics?.trackEvent('Swap', 'Fixing source changed', value);

    checkSwapWithTradeCapture({
      fixingSource: value,
    });
  };

  const handleSndFixingSourceChange = (value: string | undefined) => {
    sgwtWebAnalytics?.trackEvent('Swap', 'Fixing snd source changed', value);

    checkSwapWithTradeCapture({
      sndFixingSource: value,
    });
  };

  return (
    <div
      className="px-2 fs-12"
      style={{
        display: 'grid',
        gap: '5px',
        gridTemplateColumns: '100px 151px 20px',
        gridTemplateRows: '20px',
        alignItems: 'center',
      }}
    >
      {/*Market type*/}
      {!isCrossed && isLocalMarket && (
        <>
          <div className="text-end text-secondary">Market type</div>
          <ForwardSwitch
            name="marketType"
            identifier="marketType"
            values={['Onshore', 'Offshore'] as const}
            selectedValue={marketType}
            onChange={handleMarketTypeChange}
            data-e2e="market-type"
          />
          <div />
        </>
      )}

      {/*Cross currency*/}
      {isDefined(xCurrency) && (
        <SwapDropdownRow
          selectedValue={xCurrency}
          options={possibleXCurrencies}
          labelId="SwapTile.NDS.crossCurrency"
          dataE2e="cross-currency"
          onChange={handleXCurrencyChange}
        />
      )}

      {/*Ref. EUR/USD or Reference (no xCurrency)*/}
      <SwapDropdownRow
        selectedValue={fixingSource}
        options={possibleFixingSources}
        isOverriden={isFixingSourceOverriden}
        labelId={isDefined(xCurrency) ? 'SwapTile.NDS.fixingSource' : 'SwapTile.NDS.reference'}
        labelValues={{ currency1: currency1, currency2: xCurrency }}
        dataE2e="first-fixing-source"
        onChange={handleFixingSourceChange}
        tooltipId={
          isFixingSourceOverriden
            ? 'SwapTile.NDS.fixingSource.tooltip.overriden'
            : 'SwapTile.NDS.fixingSource.tooltip.default'
        }
        tooltipValues={{ defaultFixingSource, br: <br /> }}
      />

      {/*Ref. USD/CNY*/}
      {isDefined(sndFixingSource) && (
        <SwapDropdownRow
          selectedValue={sndFixingSource}
          options={possibleSndFixingSources}
          isOverriden={isSndFixingSourceOverriden}
          labelId="SwapTile.NDS.sndFixingSource"
          labelValues={{ currency1: xCurrency, currency2: currency2 }}
          dataE2e="second-fixing-source"
          onChange={handleSndFixingSourceChange}
          tooltipId={
            isSndFixingSourceOverriden
              ? 'SwapTile.NDS.fixingSource.tooltip.overriden'
              : 'SwapTile.NDS.fixingSource.tooltip.default'
          }
          tooltipValues={{ defaultFixingSource: defaultSndFixingSource, br: <br /> }}
        />
      )}

      {/*Near fixing date*/}
      <div className="text-end text-secondary">
        <FormattedMessage id="SwapTile.NDS.nearFixingDate" />
      </div>
      <div data-e2e="near-fixing-date">
        {isDefined(nearFixingDate) && <FormattedFXDate value={nearFixingDate} dateStyle="medium" />}
      </div>
      <div />

      {/*Far fixing date*/}
      <div className="text-end text-secondary">
        <FormattedMessage id="SwapTile.NDS.farFixingDate" />
      </div>
      <div data-e2e="far-fixing-date">
        {isDefined(farFixingDate) && <FormattedFXDate value={farFixingDate} dateStyle="medium" />}
      </div>
      <div />
    </div>
  );
};

export type SwapDropdownRowProps = {
  selectedValue: string | undefined;
  options: string[];
  labelId: IntlMessageId;
  labelValues?: Record<string, any>;
  dataE2e: string;
  onChange: (value: string) => void;
  tooltipId?: IntlMessageId;
  tooltipValues?: Record<string, any>;
  isOverriden?: boolean;
};

/**
 * Reusable component for swap dropdown rows with optional tooltip icon.
 * @param selectedValue - The currently selected value.
 * @param options - List of available options.
 * @param labelId - ID for the translated label.
 * @param labelValues - Values for the translated label.
 * @param dataE2e - Data attribute for testing.
 * @param onChange - Handler for value change.
 * @param tooltipId - Optional ID for tooltip message.
 * @param tooltipValues - Optional values for the tooltip message.
 * @param iconColor - Optional color class for the icon.
 */
const SwapDropdownRow = ({
  selectedValue,
  options,
  labelId,
  labelValues,
  dataE2e,
  onChange,
  tooltipId,
  tooltipValues,
  isOverriden = false,
}: SwapDropdownRowProps) => {
  return (
    <>
      <div className="text-end text-secondary">
        <FormattedMessage id={labelId} values={labelValues} />
      </div>

      <div>
        <Select
          data-e2e={dataE2e}
          itemsAsObjects={options}
          selectedItemValue={selectedValue}
          onChange={(option) => onChange(option)}
          className={clsx('form-select form-select-sm', { 'text-warning fw-bold': isOverriden })}
        />
      </div>

      <div>
        {tooltipId && (
          <Tooltip contents={<FormattedMessage id={tooltipId} values={tooltipValues} />} position="right">
            <i className={clsx('icon icon-sm', { 'text-warning': isOverriden })} data-e2e={`${dataE2e}-info`}>
              info_outline
            </i>
          </Tooltip>
        )}
      </div>
    </>
  );
};
